'use client'

import styled from '@emotion/styled'
import React, { useCallback, useState } from 'react'
import { Loading } from '../../atoms/Loading'
import { EditorApex } from './Apex/EditorApex'
import { EditorValorant } from './Valorant/EditorValorant'
import { EditorFortnite } from './Fortnite/EditorFortnite'
import { EditorMonsterHunterRise } from './MonsterHunterRise/EditorMonsterHunterRise'
import { Sex } from '../../../firebase/firestore/Sex'
import { Platform } from '../../../firebase/firestore/Platform'
import { VoiceChat } from '../../../firebase/firestore/VoiceChats'
import { GameTitle } from '../../../models/types/GameTitle'
import { htmlToImageString } from '../../../util/htmlToImageString'
import { EditorDBD } from './DBD/EditorDBD'
import { EditorAmongUs } from './AmongUs/EditorAmongUs'
import store from 'store'
import { useAuthState } from 'react-firebase-hooks/auth'
import { getAuth } from '../../../firebase/helpers'
import { EditorPokemonUnite } from './PokemonUnite/EditorPokemonUnite'
import { EditorSplatoon3 } from './Splatoon3/EditorSplatoon3'
import { EditorMonsterHunterRiseSunBreak } from './MonsterHunterRiseSunBreak/EditorMonsterHunterRiseSunBreak'
import { EditorFallguys } from './Fallguys/EditorFallguys'
import { EditorGenshin } from './Genshin/EditorGenshin'
import { HeaderWithBack } from '../../molecules/HeaderWithBack'
import { mapTemplateToCreatorTwitterId } from '../../../firebase/firestore/mapTemplateToCreatorTwitterId'
import { EditorOverwatch2 } from './Overwatch2/EditorOverwatch2'
import { EditorGundamEvolution } from './GundamEvolution/EditorGundamEvolution'
import { logClickEvent } from '../../../firebase/analytics'
import { useRouter, useSearchParams } from 'next/navigation'
import { EditorBlueProtocol } from './BlueProtocol/EditorBlueProtocol'
import { EditorMyHeroAcademiaUltraRumble } from './MyHeroAcademiaUltraRumble/EditorMyHeroAcademiaUltraRumble'
import { EditorBF2042 } from './BF2042/EditorBF2042'
import { EditorCodMw2 } from './CodMw2/EditorCodMw2'
import { EditorCodMw3 } from './CodMw3/EditorCodMw3'
import { EditorCodBo6 } from './CodBo6/EditorCodBo6'
import { EditorPazudora } from './Pazudora/EditorPazudora'
import { EditorMarvelRivals } from './MarvelRivals/EditorMarvelRivals'
import { EditorMonsterHunterWilds } from './MonsterHunterWilds/EditorMonsterHunterWilds'
import { EditorV2 } from './EditorV2/EditorV2'
import { FONT_FAMILIES } from '@/clients/models/types/Font'

/**
 * プラットフォーム事にフォームが違う
 *
 */

type Props = {
  gameTitle: GameTitle
  userId?: string
}

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

export const STORE_KEY_BIRTH_YEAR = 'profile-edit-birth-year-input'
export const STORE_KEY_INPUT = 'profile-edit-input'
export const STORE_KEY_IMG_STRING = 'profile-edit-img-string'

// 既存のゲームタイトルのリスト
const SUPPORTED_GAME_TITLES = [
  'apex',
  'myHeroAcademiaUltraRumble',
  'valorant',
  'dbd',
  'fortnite',
  'genshin',
  'genshinImpact',
  'amongus',
  'monsterhunterrise',
  'monsterHunterRiseSunBreak',
  'pokemonUnite',
  'splatoon3',
  'fallguys',
  'overwatch2',
  'codMw2',
  'codMw3',
  'codBo6',
  'pazudora',
  'gundamEvolution',
  'blueProtocol',
  'bf2042',
  'marvelRivals',
  'monsterHunterWilds',
]

// ゲームタイトルがサポートされているかどうかを判定する関数
const isSupportedGameTitle = (gameTitle: GameTitle): boolean => {
  return SUPPORTED_GAME_TITLES.includes(gameTitle as string)
}

export const ProfileEditor: React.FC<Props> = ({ gameTitle }) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const creatorParam = searchParams?.get('creator') || null
  const [creating, setCreating] = useState(false)
  const [submitButtonDisable, setSubmitButtonDisable] = useState(false)
  const [user] = useAuthState(getAuth())
  const onProfileComplete = useCallback(
    (
      data: any,
      targetId: string,
      selectedSex: Sex | 2,
      selectedPlatform: Array<Platform>,
      selectedVoiceChat: Array<VoiceChat>
    ) => {
      logClickEvent('ProfileCardEditorCreateClick')
      setSubmitButtonDisable(true)
      htmlToImageString(targetId).then((imgString) => {
        setCreating(true)
        data.selectedSex = selectedSex
        data.selectedPlatform = selectedPlatform
        data.selectedVoiceChat = selectedVoiceChat
        data.gameTitle = gameTitle
        data.targetId = targetId

        // set to local storage in order to pass data after login twitter
        store.set(STORE_KEY_INPUT, {
          ...data,
          creatorTwitterId: mapTemplateToCreatorTwitterId[targetId],
        })
        store.set(STORE_KEY_IMG_STRING, imgString)
        if (!user) {
          router.push('/loginOrSignup?redirectTo=/me/profiles/create/creating')
        } else {
          router.push('/me/profiles/create/creating')
        }
      })
    },
    [user, gameTitle, router]
  )
  return (
    <Container>
      <HeaderWithBack backUrl={'/me/profiles/create'} />
      {creating ? (
        <LoaderContainer>
          <Loading />
        </LoaderContainer>
      ) : (
        <>
          {/* 既存のゲームタイトルにマッチしない場合はEditorV2を表示 */}
          {!isSupportedGameTitle(gameTitle) && (
            <EditorV2
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              gameTitleId={gameTitle}
            />
          )}
          {gameTitle === 'apex' && (
            <EditorApex
              creatorParam={creatorParam}
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              fontColors={FONT_COLORS}
            />
          )}
          {gameTitle === 'myHeroAcademiaUltraRumble' && (
            <EditorMyHeroAcademiaUltraRumble
              creatorParam={creatorParam}
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              fontColors={FONT_COLORS}
            />
          )}
          {gameTitle === 'valorant' && (
            <EditorValorant
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'dbd' && (
            <EditorDBD
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'fortnite' && (
            <EditorFortnite
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {(gameTitle === 'genshin' || gameTitle === 'genshinImpact') && (
            <EditorGenshin
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'amongus' && (
            <EditorAmongUs
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'monsterhunterrise' && (
            <EditorMonsterHunterRise
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'monsterHunterRiseSunBreak' && (
            <EditorMonsterHunterRiseSunBreak
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'pokemonUnite' && (
            <EditorPokemonUnite
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'splatoon3' && (
            <EditorSplatoon3
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'fallguys' && (
            <EditorFallguys
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'overwatch2' && (
            <EditorOverwatch2
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              fontColors={FONT_COLORS}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'codMw2' && (
            <EditorCodMw2
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'codMw3' && (
            <EditorCodMw3
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'codBo6' && (
            <EditorCodBo6
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'pazudora' && (
            <EditorPazudora
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'gundamEvolution' && (
            <EditorGundamEvolution
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'blueProtocol' && (
            <EditorBlueProtocol
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              fontColors={FONT_COLORS}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'bf2042' && (
            <EditorBF2042
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
              fonts={FONT_FAMILIES}
              fontColors={FONT_COLORS}
              creatorParam={creatorParam}
            />
          )}
          {gameTitle === 'marvelRivals' && (
            <EditorMarvelRivals
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
            />
          )}
          {gameTitle === 'monsterHunterWilds' && (
            <EditorMonsterHunterWilds
              onCreate={onProfileComplete}
              submitButtonDisable={submitButtonDisable}
            />
          )}
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`

const LoaderContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`
